import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/UI';
import ColumnSelector from './ColumnSelector';
import type { ColumnConfig } from '../backtestColumns';

interface ColumnVisibilityControlProps {
    visibleColumns: Set<string>;
    onVisibilityChange: (columns: Set<string>) => void;
    columnRegistry: ColumnConfig[];
}

const ColumnVisibilityControl: React.FC<ColumnVisibilityControlProps> = ({ visibleColumns, onVisibilityChange, columnRegistry }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="relative">
            <div ref={buttonRef}>
                <Button className="flex items-center gap-2" size="sm" variant="secondary" onClick={() => setIsOpen(!isOpen)}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                        />
                        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                    </svg>
                    Columns ({visibleColumns.size})
                </Button>
            </div>

            {isOpen && (
                <div className="absolute right-0 mt-2 z-50" ref={dropdownRef}>
                    <ColumnSelector
                        columnRegistry={columnRegistry}
                        visibleColumns={visibleColumns}
                        onClose={() => setIsOpen(false)}
                        onVisibilityChange={onVisibilityChange}
                    />
                </div>
            )}
        </div>
    );
};

export default ColumnVisibilityControl;
